import {
  getEndOfDayForTz,
  getFormInputDate,
  getStartOfDayForTz,
} from './dateUtils';

const SEARCH_PERIOD_DAYS = {
  TODAY: 0,
  YESTERDAY: 1,
  LAST_3_DAYS: 3,
  LAST_7_DAYS: 7,
  LAST_14_DAYS: 14,
  LAST_30_DAYS: 30,
};

type TimelinePeriod = keyof typeof SEARCH_PERIOD_DAYS;

export type SearchPeriod = string;

export type SearchPeriodSettings = Partial<{ timeZone: string }>;

export const DEFAULT_SEARCH_PERIOD: TimelinePeriod = 'LAST_7_DAYS';

interface SearchPeriodOption {
  value: SearchPeriod;
  label: string;
}

const TIMELINE_OPTIONS: SearchPeriodOption[] = [
  { value: 'TODAY', label: 'Today' },
  { value: 'YESTERDAY', label: 'Yesterday' },
  { value: 'LAST_3_DAYS', label: 'Last 3 days' },
  { value: 'LAST_7_DAYS', label: 'Last 7 days' },
  { value: 'LAST_14_DAYS', label: 'Last 14 days' },
  { value: 'LAST_30_DAYS', label: 'Last 30 days' },
];

const isTimelinePeriod = (period: string): period is TimelinePeriod =>
  period in SEARCH_PERIOD_DAYS;

const todayIn = (options?: SearchPeriodSettings) => {
  const [year, month, day] = getFormInputDate(new Date(), options)
    .split('-')
    .map(Number);

  return [year, month - 1, day];
};

export const monthPeriodOptions = (
  months = 12,
  options?: SearchPeriodSettings
) => {
  const [year, monthIndex] = todayIn(options);

  return Array.from({ length: months }, (_, index) => {
    const date = new Date(Date.UTC(year, monthIndex - index, 1));

    return {
      value: `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    };
  });
};

export const searchPeriodOptions = (
  months = 12,
  options?: SearchPeriodSettings
) => [...TIMELINE_OPTIONS, ...monthPeriodOptions(months, options)];

const timelineRange = (
  period: TimelinePeriod,
  options?: SearchPeriodSettings
) => {
  const [year, monthIndex, day] = todayIn(options);

  return {
    startDate: getStartOfDayForTz(
      year,
      monthIndex,
      day - SEARCH_PERIOD_DAYS[period],
      options
    ),
    endDate: getEndOfDayForTz(year, monthIndex, day, options),
  };
};

const monthRange = (period: SearchPeriod, options?: SearchPeriodSettings) => {
  const [year, month] = period.split('-').map(Number);
  if (!Number.isInteger(year) || !(month >= 1 && month <= 12)) {
    return null;
  }

  return {
    startDate: getStartOfDayForTz(year, month - 1, 1, options),
    endDate: getStartOfDayForTz(year, month, 1, options),
  };
};

export const searchPeriodRange = (
  period: SearchPeriod,
  options?: SearchPeriodSettings
) =>
  isTimelinePeriod(period)
    ? timelineRange(period, options)
    : (monthRange(period, options) ??
      timelineRange(DEFAULT_SEARCH_PERIOD, options));
